import { Component, Inject, OnInit, ViewChild } from "@angular/core";
import { ClientSideListBaseComponent } from "src/app/common/core/client-side-list-base/client-side-list-base.component";
import { colDefs } from "./billing-daily-notes.config";
import { CommonModule } from "@angular/common";
import { BillingDetailsComponent } from "./billing-details.component";
import {
  CoSignBillingDetails,
  CoSignDetailsRequest,
  CoSignRequest,
} from "src/app/common/interfaces/cosign.interface";
import { StorageService } from "src/app/common/services/storage.service";
import { CoSignerClient } from "src/app/common/services/co-signer.client";
import { SecurityFeatureUtility } from "src/app/common/services/security-feature.utility";
import { Features } from "src/app/common/enums/features.enum";
import { GetRowIdParams, IRowNode, RowClassParams } from "ag-grid-community";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmationModalComponent } from "src/app/common/components/core/modal/confirmation-modal/confirmation-modal.component";
import { ChangesSaved, ErrorString, Ok, SomethingWentWrong } from "src/app/common/constants/core-constants";
import { CosignSearchComponent } from "../cosign-search/cosign-search.component";

@Component({
  selector: "app-billing-daily-notes",
  templateUrl: "./billing-daily-notes.component.html",
  styleUrls: ["./billing-daily-notes.component.scss"],
  standalone: true,
  imports: [
    ClientSideListBaseComponent,
    CommonModule,
    CosignSearchComponent
],
})
export class BillingDailyNotesComponent implements OnInit {
  currentUser: string = "";
  loggedInUser: string | null = null;
  currentFacility: string | null = null;

  searchText: string = "";
  showArchived: boolean = false;
  enableSign: boolean = false;
  selectedRowsToSign: number[] = [];

  cosignPermissions: { canView: boolean, canManage: boolean } = {
    canView: false,
    canManage: false
  };

  columnDefs: any[] = [];
  cosignBillingData: CoSignBillingDetails[] = [];
  detailCellRenderer = BillingDetailsComponent;
  detailCellRendererParams = {
    getDetailRowData: (params: any) => {
      if (!params.data) {
        params.successCallback([]);
        return;
      }
      if (typeof params.successCallback !== "function") {
        return;
      }
      params.successCallback(params.data);
    },
  };

  @ViewChild(ClientSideListBaseComponent)
  theraclient!: ClientSideListBaseComponent;

  constructor(
    @Inject("userId") public userId: string,
    private cosignClient: CoSignerClient,
    private storageService: StorageService,
    private dialog: MatDialog,
    private securityFeatureUtility: SecurityFeatureUtility
  ) {
  }

  ngOnInit(): void {
    this.fetchCosignPermission();
    this.currentUser = this.userId;
    this.loggedInUser = this.storageService.getUserId();
    this.currentFacility = this.storageService.getSelectedFacilityId();
    this.columnDefs = colDefs(this);
    this.getCoSignBillingData(this.showArchived, this.searchText);
  }

  getCoSignBillingData(archivedOnly: boolean, searchValue: string): void {
    if (!this.currentUser || !this.currentFacility) {
      return;
    }

    const trimmedSearchValue = searchValue.trim();

    const request: CoSignDetailsRequest = {
      facilityId: this.currentFacility,
      cosignerId: this.currentUser,
      archivedOnly,
      searchString: trimmedSearchValue ? trimmedSearchValue : undefined
    };

    this.cosignClient
      .getCoSignBillingDetails(request)
      .subscribe({
        next: (data: any) => {
          this.cosignBillingData = data.responseData;
          this.syncSelections();
        },
        error: (error) => this.handleError(error)
      });
  }

  selectedForCoSign($event: any) {
    this.syncSelections();
  }

  private syncSelections(): void {
    const selectedRows: CoSignBillingDetails[] = this.theraclient?.getSelectedRows() ?? [];

    this.selectedRowsToSign = selectedRows.map(x => x.id);
    this.enableSign =
      selectedRows.length > 0 &&
      selectedRows.every(x => x.isSignEnabled);
  }

  onSearch(value: string): void {
    this.searchText = value;
    this.getCoSignBillingData(this.showArchived, this.searchText);
  }

  onShowArchived(value: boolean): void {
    this.showArchived = value;
    this.getCoSignBillingData(this.showArchived, this.searchText);
  }

  onClickSign(): void {
    if (!this.loggedInUser || this.selectedRowsToSign.length === 0 || !this.enableSign) {
      return;
    }

    const cosignBillingRequest: CoSignRequest = {
      cosignedby: this.loggedInUser,
      ids: this.selectedRowsToSign,
    };

    this.cosignClient
      .signBillingDetails(cosignBillingRequest)
      .subscribe(
        {
          next: (data: any) => this.handleSuccess(),
          error: (error) => this.handleError(error)
        });
  }

  isRowMaster(data: any): boolean {
    return true;
  }

  isRowSelectable = (params: IRowNode<CoSignBillingDetails>) => {
    if (!params.data) return false;
    if (!this.cosignPermissions.canManage) return false;
    if (!params.data.isSignEnabled) return false;
    if (params.data.isArchived) return false;
    return params.data.isSignEnabled === true;
  }

  getRowId = (params: GetRowIdParams<CoSignBillingDetails>): string => String(params.data?.id);

  getRowClass = (params: RowClassParams<CoSignBillingDetails>): string => 'row-expanded';

  private fetchCosignPermission(): void {
    this.cosignPermissions = this.securityFeatureUtility.getPermissions(Features.CO_SIGN);
  }

  private handleSuccess() {
    this.dialog.open(ConfirmationModalComponent, {
      data: ChangesSaved,
    })
      .afterClosed()
      .subscribe(() => {
        this.getCoSignBillingData(this.showArchived, this.searchText);
      });
  }

  private handleError(error: any) {
    this.dialog.open(ConfirmationModalComponent, {
      data: {
        title: ErrorString,
        message: error?.error?.ResponseMessage ?? SomethingWentWrong,
        hideCancelButton: true,
        buttonText: Ok,
        isError: true
      }
    });
  };
}